import { SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

import { HighlightColorMenu } from './HighlightColorMenu';
import { HIGHLIGHT_COLORS } from './highlightConstants';
import { useActiveHighlightColor } from './useActiveHighlightColor';

interface UseHighlightButtonProps {
  /** Whether the caller's popover is currently open. */
  isOpen: boolean;
  /** Called after a color is applied or removed, so the caller can close
   *  their popover. */
  onClose: () => void;
}

interface UseHighlightButtonReturn {
  /** True when the popover is open OR the caret sits inside a highlight. */
  isActive: boolean;
  /** sx overlay that makes the trigger background mirror the active highlight
   *  color. `undefined` when the caret is not inside a highlight. */
  activeColorSx: SxProps<Theme> | undefined;
  /** The color picker menu, ready to be rendered inside the caller's popover. */
  menu: ReactNode;
}

/**
 * Shared behavior for any highlight toolbar button. Centralizes the caret
 * color tracking, the active-state computation, the active-color background
 * styling, and the color menu so multiple button shells (e.g. default toolbar
 * vs slide editor toolbar) stay behaviorally identical even if their
 * popover/visual shells differ.
 */
export function useHighlightButton({
  isOpen,
  onClose,
}: UseHighlightButtonProps): UseHighlightButtonReturn {
  const activeHighlightColor = useActiveHighlightColor();

  const isActive = isOpen || activeHighlightColor !== null;

  const activeColorSx: SxProps<Theme> | undefined =
    activeHighlightColor !== null
      ? {
          bgcolor: HIGHLIGHT_COLORS[activeHighlightColor],
          '&:hover': {
            bgcolor: HIGHLIGHT_COLORS[activeHighlightColor],
          },
        }
      : undefined;

  const menu = (
    <HighlightColorMenu
      activeHighlightColor={activeHighlightColor}
      onAfterSelect={onClose}
    />
  );

  return { isActive, activeColorSx, menu };
}
